/**
 * Lightweight composable that provides construction breadcrumb data
 * without importing the heavy construction composables (which pull in ~100KB of GQL queries).
 *
 * Reads directly from pinia raw state by store ID to avoid the circular import chain:
 *   store/construction-*.ts <-> composables/construction*.ts (imports all GQL)
 */
import { getActivePinia } from 'pinia';
import { computed } from 'vue';

import type { BreadCrumbItem } from '@/components/atoms/breadcrumb-fi/BreadcrumbFi.vue';
import { useDict, useFischer } from '@/composables/fischerPlugin';

const formatNameForUrl = (name: string) => {
  return name
    .replace(/[^A-Za-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+$/, '')
    .toLowerCase();
};

const getPathString = (store: Record<string, any>, pageType: string) => {
  const data = store[pageType];
  if (data) {
    const name = formatNameForUrl(data.name);
    const id = store[`${pageType}Id`];
    if (typeof id !== 'string') {
      return '';
    }
    return encodeURI(`/${name}_${id.replace(/.*:/, '')}`);
  }
  return '';
};

export const useConstructionBreadcrumb = () => {
  const fischer = useFischer();
  const dict = useDict();
  const pinia = getActivePinia();

  const getCDBreadcrumb = computed<BreadCrumbItem[] | null>(() => {
    const cdStore = pinia?.state.value?.['construction-documentation'];
    const pageUrl = fischer.pages.constructionDocumentation()?.url;
    if (!cdStore || IS_SSR || !pageUrl || !window.location.href.includes(pageUrl)) {
      return null;
    }

    const breadcrumbs: BreadCrumbItem[] = [];
    const host = cdStore.isArchive ? pageUrl + '/archive' : pageUrl;

    if (IS_SHOWROOM && window.location.href.includes(pageUrl)) {
      breadcrumbs.push({
        url: pageUrl,
        title:
          dict.get('construction-Projects.Breadcrumb.Construction-Documentation') ||
          'Construction Documentation',
      });
    }

    if (cdStore.isArchive) {
      breadcrumbs.push({
        url: host,
        title: dict.get('construction-Projects.Breadcrumb.Archive') || 'Archive',
      });
    }

    if (cdStore.project) {
      breadcrumbs.push({
        url: host + getPathString(cdStore, 'project'),
        title: cdStore.project.name,
      });
    }

    if (cdStore.area) {
      breadcrumbs.push({
        url: host + getPathString(cdStore, 'project') + getPathString(cdStore, 'area'),
        title: cdStore.area.name,
      });
    }

    if (cdStore.room) {
      breadcrumbs.push({
        url:
          host +
          getPathString(cdStore, 'project') +
          getPathString(cdStore, 'area') +
          getPathString(cdStore, 'room'),
        title: cdStore.room.name,
      });
    }

    if (cdStore.firestop) {
      breadcrumbs.push({
        url:
          host +
          getPathString(cdStore, 'project') +
          getPathString(cdStore, 'area') +
          getPathString(cdStore, 'room') +
          getPathString(cdStore, 'firestop'),
        title: cdStore.firestop.name,
      });
    }

    if (cdStore.pageType === 'add-documentation-point' && !cdStore.isLoading) {
      breadcrumbs.push({
        url: '',
        title:
          dict.get('construction-Projects.Breadcrumb.Add-Documentation-Point') ||
          'Add Documentation Point',
      });
    }

    if (cdStore.pageType === 'audit-log' && !cdStore.isLoading) {
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.Audit-Log') || 'Audit Log',
      });
    }

    if (cdStore.pageType === 'reports' && !cdStore.isLoading) {
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.Reports') || 'Reports',
      });
    }

    if (cdStore.pageType === 'ej-documents' && !cdStore.isLoading) {
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.EJ-Documents') || 'EJ Documents',
      });
    }

    if (cdStore.pageType === 'third-party-products' && !cdStore.isLoading) {
      breadcrumbs.push({
        url: '',
        title:
          dict.get('construction-Projects.Breadcrumb.Third-Party-Products') ||
          'Third Party Products',
      });
    }

    if (cdStore.pageType === 'manage-participants' && !cdStore.isLoading) {
      breadcrumbs.push({
        url: '',
        title:
          dict.get('construction-Projects.Breadcrumb.Manage-Participants') || 'Manage Participants',
      });
    }

    if (cdStore.pageType === 'search' && !cdStore.isLoading) {
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.Search') || 'Search',
      });
    }

    return breadcrumbs;
  });

  const getCMBreadcrumb = computed<BreadCrumbItem[] | null>(() => {
    const cmStore = pinia?.state.value?.['construction-monitoring'];
    const pageUrl = fischer.pages.constructionMonitoring()?.url;
    if (
      !cmStore ||
      IS_SSR ||
      !pageUrl ||
      !window.location.href.includes(pageUrl) ||
      cmStore.isLoading
    ) {
      return null;
    }

    const breadcrumbs: BreadCrumbItem[] = [];

    if (IS_SHOWROOM && window.location.href.includes(pageUrl)) {
      breadcrumbs.push({
        url: pageUrl,
        title:
          dict.get('construction-Projects.Breadcrumb.Construction-Monitoring') ||
          'Construction Monitoring',
      });
    }

    if (cmStore.project) {
      breadcrumbs.push({
        url: pageUrl + getPathString(cmStore, 'project'),
        title: cmStore.project.name,
      });
    }

    if (cmStore.area) {
      breadcrumbs.push({
        url: pageUrl + getPathString(cmStore, 'project') + getPathString(cmStore, 'area'),
        title: cmStore.area.name,
      });
    }

    if (cmStore.room) {
      breadcrumbs.push({
        url:
          pageUrl +
          getPathString(cmStore, 'project') +
          getPathString(cmStore, 'area') +
          getPathString(cmStore, 'room'),
        title: cmStore.room.name,
      });
    }

    if (cmStore.pageType === 'sensor') {
      const title =
        cmStore.sensor?.name ||
        dict.get('construction-Projects.Breadcrumb.Add-Documentation-Point') ||
        'Add Documentation Point';

      breadcrumbs.push({
        url: '',
        title,
      });
    }

    if (cmStore.pageType === 'manage-participants' && !cmStore.isLoading) {
      breadcrumbs.push({
        url: '',
        title:
          dict.get('construction-Projects.Breadcrumb.Manage-Participants') || 'Manage Participants',
      });
    }

    if (cmStore.pageType === 'search' && !cmStore.isLoading) {
      breadcrumbs.push({
        url: '',
        title: dict.get('construction-Projects.Breadcrumb.Search') || 'Search',
      });
    }

    return breadcrumbs;
  });

  return { getCDBreadcrumb, getCMBreadcrumb };
};
